// Vector General Arrangement (Deck 2 / Tween Deck plan view) for the chem fleet.
// Drawn from the reference photo in src/assets/chem-tank-plan-reference.jpeg.
//
// Coordinate space: 1400 x 480 SVG units (wider beam for clearance).

export type TankGroup =
  | "cargo"
  | "ballast"
  | "void"
  | "fuel"
  | "service"
  | "slop"
  | "machinery"
  | "area";

export type TankShape = {
  id: string;
  label: string;
  group: TankGroup;
  shape?: "rect" | "circle";
  x?: number; y?: number; w?: number; h?: number;
  cx?: number; cy?: number; r?: number;
  capacity?: number;
  unit?: "m³" | "t";
  fillable?: boolean;
  rounded?: number;
  sub?: string;
};

export const PLAN_W = 1400;
export const PLAN_H = 480;

// Hull (centerline at y=240, beam ≈ 380px)
export const HULL_PATH =
  "M 60,50 " +
  "Q 25,50 25,100 " +
  "L 25,380 " +
  "Q 25,430 60,430 " +
  "L 1080,430 " +
  "Q 1240,430 1370,240 " +
  "Q 1240,50 1080,50 " +
  "Z";

export const INNER_HULL_PATH =
  "M 75,72 L 1075,72 Q 1210,72 1330,240 Q 1210,408 1075,408 L 75,408 Z";

export const FRAME_MARKS = [-10, 0, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 100, 110, 120, 125, 140];

// ── Cargo block: 5 columns × 2 rows of circular tanks ────────────────────
// Starboard row is offset forward (right) by half a pitch — staggered layout
// matching the actual GA where stbd tanks sit between port tank frames.
const CARGO_LEFT  = 380;
const CARGO_PITCH = 105;
const CARGO_R     = 44;
const STBD_OFFSET = 52;          // ½ pitch fwd shift for starboard row
const ROW_TOP_CY  = 186;
const ROW_BOT_CY  = 298;

export function buildCargoBlock(): TankShape[] {
  const out: TankShape[] = [];
  for (let i = 0; i < 5; i++) {
    const tankNo = i + 1;
    out.push({
      id: `${tankNo}P`, label: `${tankNo}P`, group: "cargo",
      shape: "circle", cx: CARGO_LEFT + i * CARGO_PITCH, cy: ROW_TOP_CY, r: CARGO_R,
      capacity: 540, unit: "m³", fillable: true, sub: "Special Product",
    });
    out.push({
      id: `${tankNo}S`, label: `${tankNo}S`, group: "cargo",
      shape: "circle", cx: CARGO_LEFT + i * CARGO_PITCH + STBD_OFFSET, cy: ROW_BOT_CY, r: CARGO_R,
      capacity: 540, unit: "m³", fillable: true, sub: "Special Product",
    });
  }
  return out;
}

const rect = (
  id: string, label: string, group: TankGroup,
  x: number, y: number, w: number, h: number,
  extra: Partial<TankShape> = {}
): TankShape => ({ id, label, group, shape: "rect", x, y, w, h, rounded: 2, ...extra });

// Y-band reference (centerline 240, double-hull layout, vertically centered):
//   Outer top WBT (P): y=64,  h=18  → 64-82
//   Top wings:         y=86,  h=48  → 86-134
//   Cargo top circles: cy=186, r=44 → 142-230
//   Cargo trunk:       y=233, h=18  → 233-251
//   Cargo bot circles: cy=298, r=44 → 254-342
//   Bottom wings:      y=346, h=48  → 346-394
//   Outer bot WBT (S): y=398, h=18  → 398-416
//   ⇒ span 64-416, center = 240 ✓

const WBT_X0 = 275;
const WBT_SEG_W = 117;

function buildWingBallast(): TankShape[] {
  const out: TankShape[] = [];
  for (let i = 0; i < 5; i++) {
    const x = WBT_X0 + i * WBT_SEG_W;
    out.push(rect(`WBT-${i + 1}P`, `${i + 1}P WBT`, "ballast", x, 64, WBT_SEG_W - 3, 18,
      { capacity: 140, unit: "m³", fillable: true }));
    out.push(rect(`WBT-${i + 1}S`, `${i + 1}S WBT`, "ballast", x, 398, WBT_SEG_W - 3, 18,
      { capacity: 140, unit: "m³", fillable: true }));
  }
  return out;
}

export const PLAN_TANKS: TankShape[] = [
  // ── AFT (stern, left) ────────────────────────────────────────────
  rect("PROP-RM",    "Propulsion Room", "machinery", 32,  170, 90, 140, { fillable: false, rounded: 6 }),
  rect("HULL-COIL-P","Hull Coil P",     "service",   126, 86,  70, 48,  { capacity: 12, unit: "t", fillable: true }),
  rect("HULL-COIL-S","Hull Coil S",     "service",   126, 346, 70, 48,  { capacity: 12, unit: "t", fillable: true }),
  rect("BOW-THR-AFT","Bow Thr. Sw.",    "machinery", 126, 175, 70, 130, { fillable: false }),
  rect("WBT-AFT-P",  "Aft WBT P",       "ballast",   126, 64,  70, 18,  { capacity: 80, unit: "m³", fillable: true }),
  rect("WBT-AFT-S",  "Aft WBT S",       "ballast",   126, 398, 70, 18,  { capacity: 80, unit: "m³", fillable: true }),

  // ── Bunker block forward of propulsion ──────────────────────────
  rect("DFO-AFT-P", "Domestic FO P",   "fuel", 200, 86,  70, 48, { capacity: 35, unit: "t", fillable: true, sub: "DTL=2 / 30 m³" }),
  rect("DFO-AFT-S", "Domestic FO S",   "fuel", 200, 346, 70, 48, { capacity: 35, unit: "t", fillable: true, sub: "DTL=2 / 30 m³" }),
  rect("FO-AFT-P",  "Fuel Oil P",      "fuel", 200, 175, 70, 60, { capacity: 95, unit: "t", fillable: true }),
  rect("FO-AFT-S",  "Fuel Oil S",      "fuel", 200, 245, 70, 60, { capacity: 95, unit: "t", fillable: true }),
  rect("WBT-BNK-P", "Bunker WBT P",    "ballast", 200, 64,  70, 18, { capacity: 80, unit: "m³", fillable: true }),
  rect("WBT-BNK-S", "Bunker WBT S",    "ballast", 200, 398, 70, 18, { capacity: 80, unit: "m³", fillable: true }),

  // ── Side ballast wings (double hull) along cargo block ──────────
  ...buildWingBallast(),

  // ── Top wing tanks (above cargo circles) ────────────────────────
  rect("SLOP-1P",  "Slop Prod P1", "slop", 275, 86, 95, 48, { capacity: 60,  unit: "m³", fillable: true, sub: "DTL=2 / 50 m³" }),
  rect("SLOP-2P",  "Slop Prod P2", "slop", 373, 86, 95, 48, { capacity: 60,  unit: "m³", fillable: true, sub: "DTL=2 / 50 m³" }),
  rect("FO-MID-P", "Fuel Oil P",   "fuel", 471, 86, 95, 48, { capacity: 110, unit: "t",  fillable: true }),
  rect("DFO-MID-P","Dom. FO P",    "fuel", 569, 86, 95, 48, { capacity: 30,  unit: "t",  fillable: true }),
  rect("DRIP-P",   "Drip Tray P",  "void", 667, 86, 95, 48, { fillable: false, sub: "Pipe / cable duct" }),
  rect("FO-FWD-P", "Fuel Oil P",   "fuel", 765, 86, 95, 48, { capacity: 90,  unit: "t",  fillable: true }),

  // ── Bottom wing tanks (below cargo circles) ─────────────────────
  rect("SLOP-1S",  "Slop Prod S1", "slop", 275, 346, 95, 48, { capacity: 60,  unit: "m³", fillable: true, sub: "DTL=2 / 50 m³" }),
  rect("SLOP-2S",  "Slop Prod S2", "slop", 373, 346, 95, 48, { capacity: 60,  unit: "m³", fillable: true, sub: "DTL=2 / 50 m³" }),
  rect("FO-MID-S", "Fuel Oil S",   "fuel", 471, 346, 95, 48, { capacity: 110, unit: "t",  fillable: true }),
  rect("DFO-MID-S","Dom. FO S",    "fuel", 569, 346, 95, 48, { capacity: 30,  unit: "t",  fillable: true }),
  rect("SVC-TRUNK","Service Trunk","area", 667, 346, 95, 48, { fillable: false, sub: "Cargo piping" }),
  rect("FO-FWD-S", "Fuel Oil S",   "fuel", 765, 346, 95, 48, { capacity: 90,  unit: "t",  fillable: true }),

  // ── Cargo tanks (5 × 2, stbd row staggered fwd) ────────────────
  ...buildCargoBlock(),

  // Centerline cargo passage / pipe trunk between rows
  rect("CARGO-TRUNK", "Cargo Piping C/D", "area", 365, 233, 540, 18, { fillable: false }),

  // ── Forward cofferdam / pump room / office ─────────────────────
  rect("COFF-FWD", "Cofferdam",       "void",      935, 64,  30, 352, { fillable: false }),
  rect("PUMP-RM",  "Pump Room",       "machinery", 970, 64,  60, 352, { fillable: false }),
  rect("ECR",      "Office / ECR",    "area",      1035, 64, 70, 176, { fillable: false, sub: "Engine Control" }),
  rect("WORKSHOP", "Workshop",        "area",      1035, 240, 70, 176, { fillable: false }),

  // ── Forward bunker pair just inside bow ────────────────────────
  rect("HFO-FWD-P", "HFO Fwd P",      "fuel", 1110, 86, 60, 100, { capacity: 140, unit: "t", fillable: true }),
  rect("HFO-FWD-S", "HFO Fwd S",      "fuel", 1110, 266, 60, 100, { capacity: 140, unit: "t", fillable: true }),
  rect("EL-WORKSHOP","Elec Workshop", "area", 1180, 156, 60, 60,  { fillable: false }),
  rect("EL-EQUIP",  "Elec Equip Rm",  "area", 1180, 236, 60, 60,  { fillable: false }),

  // ── Fore peak ──────────────────────────────────────────────────
  rect("FORE-PEAK", "Fore Peak",      "void", 1245, 190, 80, 100, { fillable: false, rounded: 6 }),
];

export const GROUP_FILL: Record<TankGroup, string> = {
  cargo:     "hsl(195 75% 45%)",
  ballast:   "hsl(220 70% 55%)",
  void:      "hsl(215 14% 35%)",
  fuel:      "hsl(28 85% 50%)",
  service:   "hsl(160 55% 42%)",
  slop:      "hsl(20 85% 48%)",
  machinery: "hsl(215 18% 30%)",
  area:      "hsl(215 18% 38%)",
};

export const GROUP_LABEL: Record<TankGroup, string> = {
  cargo:     "Cargo (Special Product)",
  ballast:   "Ballast (WBT)",
  void:      "Void / Cofferdam",
  fuel:      "Fuel (HFO/MGO/DFO)",
  service:   "Hull Coil / Service",
  slop:      "Slop Product",
  machinery: "Machinery",
  area:      "Area / Trunk",
};
